import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSse, useSseSubscription } from '../context/SseContext';
import { getPendingApprovals } from '../api';

const TOKENS = {
  headerBg: 'oklch(0.18 0.014 260)',
  headerInk: 'oklch(0.98 0.004 250)',
  sidebarBg: 'oklch(0.20 0.012 260)',
  sidebarBorder: 'oklch(0.14 0.012 260)',
  sidebarHover: 'oklch(0.28 0.014 260)',
  sidebarInk: 'oklch(0.75 0.01 250)',
  sidebarInkActive: 'oklch(0.98 0.004 250)',
  sidebarMeta: 'oklch(0.60 0.01 250)',
  ok: 'oklch(0.62 0.13 150)',
};
const UI_FONT = 'var(--font-ui)';
const MONO_FONT = 'var(--font-ui)';

// Persisted under this key so the user's preferred mode survives reloads
// and applies across pages.
const COLLAPSED_STORAGE_KEY = 'erbi.sidebar.collapsed';
const SIDEBAR_WIDTH_EXPANDED = 220;
const SIDEBAR_WIDTH_COLLAPSED = 56;
const TOP_BAR_HEIGHT = 44;

// `roles` controls visibility. Items without `roles` are visible to every
// authenticated user; items with `roles` are filtered to those roles only.
// Settings is processor/admin-only since it deals with routing + extraction
// policy that approvers don't own.
const NAV_ITEMS = [
  { path: '/board', label: 'Invoice Board', icon: 'M3 6a2 2 0 0 1 2-2h4v16H5a2 2 0 0 1-2-2V6zM10 4h4v16h-4zM15 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4V4z' },
  { path: '/vat', label: 'VAT', icon: 'M19 5 5 19M6.5 4a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM17.5 15a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z' },
  { path: '/luxembourg', label: 'Luxembourg', icon: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7' },
  { path: '/tracker', label: 'Payment Tracker', icon: 'M3 3v18h18M19 9l-5 5-4-4-3 3' },
  { path: '/reconciliation', label: 'Reconciliation', icon: 'M8 3 4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4' },
  { path: '/budget', label: 'Budget Workbench', icon: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' },
  { path: '/projects', label: 'Projects', icon: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z' },
  { path: '/vendors', label: 'Vendors', icon: 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18ZM6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2M10 6h4M10 10h4M10 14h4M10 18h4' },
  { path: '/paying-banks', label: 'Paying Banks', icon: 'M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3' },
  { path: '/company-codes', label: 'Company Codes', icon: 'M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16' },
  {
    path: '/users',
    label: 'Users',
    icon: 'M16 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M14 17v4M18 19h-4',
    roles: ['admin'],
  },
  { path: '/classifications', label: 'Mappings', icon: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' },
  {
    path: '/settings',
    label: 'Settings',
    icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.55 1.7 1.7 0 0 0-1.87.33l-.06.06a2 2 0 1 1-2.83-2.82l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.64 8.6a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.09c0 .68.39 1.3 1.03 1.55a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9c.25.64.87 1.03 1.55 1.03H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1.03z',
    roles: ['processor', 'admin'],
  },
];

const APPROVALS_ICON =
  'M9 12l2 2 4-4m-5 9a9 9 0 1 1 0-18 9 9 0 0 1 0 18z';

// Pinned to the bottom of the sidebar (just above the user block) rather than
// living inline in NAV_ITEMS — it's a reference link, not a primary section.
const TUTORIAL_ITEM = {
  path: '/budget/tutorial',
  label: 'Budget Tutorial',
  icon: 'M2 3h20M4 3v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V3M10 6.5l4 2.5-4 2.5zM12 15v3M8 21l4-3 4 3',
};

function readCollapsedPreference() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function NavHeader({ actions, endActions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, setCurrentUser } = useUser();
  const { sseConnected } = useSse();

  const [pendingCount, setPendingCount] = useState(0);
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);

  const sidebarWidth = collapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  const loadPendingCount = useCallback(async () => {
    if (currentUser?.role !== 'approver') return;
    try {
      const res = await getPendingApprovals();
      setPendingCount(Array.isArray(res.data) ? res.data.length : 0);
    } catch {
      // ignore
    }
  }, [currentUser?.role]);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  // Refresh the pending-approvals badge on every stream message (same
  // behavior as before — the shared EventSource just fans the event in).
  useSseSubscription(() => loadPendingCount());

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // localStorage unavailable — preference just won't persist this session.
      }
      return next;
    });
  };

  const handleLogout = () => {
    setCurrentUser(null);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const currentItem = NAV_ITEMS.find((n) => isActive(n.path));
  const currentLabel = isActive('/approvals')
    ? 'Approvals'
    : currentItem?.label || '';

  return (
    <>
      {/* Inject body padding so every page is shifted right of the sidebar
          and below the top bar, with no per-page edits required. */}
      <style>{`
        body {
          padding-left: ${sidebarWidth}px;
          padding-top: ${TOP_BAR_HEIGHT}px;
          transition: padding-left .18s ease;
        }
      `}</style>

      <header
        style={{
          position: 'fixed',
          top: 0,
          left: sidebarWidth,
          right: 0,
          height: TOP_BAR_HEIGHT,
          background: TOKENS.headerBg,
          color: TOKENS.headerInk,
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          gap: 12,
          borderBottom: `1px solid ${TOKENS.sidebarBorder}`,
          fontFamily: UI_FONT,
          zIndex: 30,
          transition: 'left .18s ease',
        }}
      >
        <span
          title={
            sseConnected ? 'Live updates active' : 'Live updates disconnected'
          }
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: sseConnected ? TOKENS.ok : 'oklch(0.48 0.15 25)',
          }}
        />
        {currentLabel && (
          <span
            style={{
              fontFamily: MONO_FONT,
              fontSize: 10.5,
              color: 'oklch(0.65 0.01 250)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {currentLabel}
          </span>
        )}

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          {actions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {actions}
            </div>
          )}
          {endActions}
        </div>
      </header>

      <aside
        aria-label="Primary navigation"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: sidebarWidth,
          background: TOKENS.sidebarBg,
          color: TOKENS.sidebarInk,
          borderRight: `1px solid ${TOKENS.sidebarBorder}`,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: UI_FONT,
          zIndex: 40,
          transition: 'width .18s ease',
        }}
      >
        <div
          onClick={() => navigate('/board')}
          style={{
            height: TOP_BAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? 0 : '0 14px',
            cursor: 'pointer',
            borderBottom: `1px solid ${TOKENS.sidebarBorder}`,
            color: TOKENS.sidebarInkActive,
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: '-0.025em',
          }}
        >
          {collapsed ? 'E' : 'Erbi'}
        </div>

        <nav
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '8px 6px',
            gap: 2,
            overflowY: 'auto',
          }}
        >
          {NAV_ITEMS
            .filter((item) => !item.roles || item.roles.includes(currentUser?.role))
            .map((item) => (
              <SidebarLink
                key={item.path}
                icon={item.icon}
                label={item.label}
                active={isActive(item.path)}
                collapsed={collapsed}
                onClick={() => navigate(item.path)}
              />
            ))}
          {currentUser?.role === 'approver' && (
            <SidebarLink
              icon={APPROVALS_ICON}
              label="Approvals"
              active={isActive('/approvals')}
              collapsed={collapsed}
              onClick={() => navigate('/approvals')}
              badge={pendingCount > 0 ? pendingCount : null}
            />
          )}
        </nav>

        <div style={{ marginTop: 'auto' }}>
          {/* Budget Tutorial — pinned just above the user block, same link
              component/styling/active-state as the main nav items (so the
              collapsed-mode icon + tooltip behave identically). */}
          <div
            style={{
              borderTop: `1px solid ${TOKENS.sidebarBorder}`,
              padding: '8px 6px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <SidebarLink
              icon={TUTORIAL_ITEM.icon}
              label={TUTORIAL_ITEM.label}
              active={isActive(TUTORIAL_ITEM.path)}
              collapsed={collapsed}
              onClick={() => navigate(TUTORIAL_ITEM.path)}
            />
          </div>

          <div
            style={{
              borderTop: `1px solid ${TOKENS.sidebarBorder}`,
              padding: collapsed ? '8px 0' : '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              alignItems: collapsed ? 'center' : 'stretch',
            }}
          >
          {!collapsed && (
            <div
              style={{
                fontSize: 11.5,
                color: TOKENS.sidebarInk,
                lineHeight: 1.3,
              }}
            >
              {currentUser?.name}
              <br />
              <span
                style={{
                  fontSize: 10,
                  color: TOKENS.sidebarMeta,
                  fontFamily: MONO_FONT,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {currentUser?.role}
              </span>
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Log out"
            aria-label="Log out"
            style={{
              padding: collapsed ? '6px' : '6px 8px',
              borderRadius: 5,
              border: `1px solid oklch(0.28 0.012 260)`,
              background: 'transparent',
              color: 'oklch(0.80 0.01 250)',
              fontFamily: UI_FONT,
              fontSize: 11.5,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
            {!collapsed && <span>Logout</span>}
          </button>
          <button
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            style={{
              padding: collapsed ? '6px' : '6px 8px',
              borderRadius: 5,
              border: `1px solid oklch(0.28 0.012 260)`,
              background: 'transparent',
              color: 'oklch(0.70 0.01 250)',
              fontFamily: UI_FONT,
              fontSize: 11,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform .18s ease',
              }}
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
            {!collapsed && <span>Collapse</span>}
          </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function SidebarLink({ icon, label, active, collapsed, onClick, badge }) {
  const buttonRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);

  // Position the tooltip flush against the right edge of the icon button
  // and vertically centered. Recompute right before paint so the first
  // frame after hover already places it correctly (no jitter).
  useLayoutEffect(() => {
    if (!collapsed || !hovered || !buttonRef.current) {
      setTooltipPos(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  }, [collapsed, hovered]);

  const showTooltip = collapsed && hovered && tooltipPos != null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label={label}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10,
          padding: collapsed ? '8px 0' : '8px 10px',
          borderRadius: 6,
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          background: active ? TOKENS.sidebarHover : 'transparent',
          color: active ? TOKENS.sidebarInkActive : TOKENS.sidebarInk,
          fontFamily: UI_FONT,
          fontSize: 12.5,
          fontWeight: active ? 600 : 500,
          position: 'relative',
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flex: '0 0 auto' }}
        >
          <path d={icon} />
        </svg>
        {!collapsed && <span>{label}</span>}
        {badge != null && (
          <span
            style={{
              marginLeft: collapsed ? 0 : 'auto',
              position: collapsed ? 'absolute' : 'static',
              top: collapsed ? 2 : undefined,
              right: collapsed ? 4 : undefined,
              background: 'oklch(0.48 0.15 25)',
              color: '#fff',
              fontFamily: MONO_FONT,
              fontSize: collapsed ? 9 : 10,
              padding: collapsed ? '0 4px' : '1px 6px',
              borderRadius: 8,
              lineHeight: collapsed ? '14px' : 'normal',
              minWidth: collapsed ? 14 : undefined,
              textAlign: 'center',
            }}
          >
            {badge}
          </span>
        )}
      </button>
      {showTooltip &&
        createPortal(
          <SidebarTooltip top={tooltipPos.top} left={tooltipPos.left} label={label} />,
          document.body
        )}
    </>
  );
}

// Floating label that appears next to a collapsed sidebar icon on hover.
// Rendered through a portal so the sidebar's clipping context can't crop it.
function SidebarTooltip({ top, left, label }) {
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        top,
        left,
        transform: 'translateY(-50%)',
        zIndex: 60,
        pointerEvents: 'none',
        animation: 'erbi-tooltip-in .12s ease-out',
        fontFamily: UI_FONT,
      }}
    >
      <style>{`
        @keyframes erbi-tooltip-in {
          from { opacity: 0; transform: translate(-4px, -50%); }
          to   { opacity: 1; transform: translate(0, -50%); }
        }
      `}</style>
      <div
        style={{
          position: 'relative',
          background: 'oklch(0.22 0.012 260)',
          color: 'oklch(0.98 0.004 250)',
          padding: '6px 11px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '0.005em',
          whiteSpace: 'nowrap',
          border: '1px solid oklch(0.30 0.012 260)',
          boxShadow:
            '0 8px 24px -8px rgba(0,0,0,0.45), 0 2px 6px -2px rgba(0,0,0,0.35)',
        }}
      >
        {label}
        {/* Arrow pointing back at the icon (left edge of tooltip). */}
        <span
          style={{
            position: 'absolute',
            left: -5,
            top: '50%',
            width: 9,
            height: 9,
            transform: 'translateY(-50%) rotate(45deg)',
            background: 'oklch(0.22 0.012 260)',
            borderLeft: '1px solid oklch(0.30 0.012 260)',
            borderBottom: '1px solid oklch(0.30 0.012 260)',
          }}
        />
      </div>
    </div>
  );
}

export default NavHeader;
